/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SystemMiscActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Component for rendering action buttons in System Misc DataGrid.
────────────────────────────────────────────────────────────────*/

import { Edit, RefreshCw } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { SystemMiscConfig } from '../types';

interface SystemMiscActionsProps {
  config: SystemMiscConfig;
  refreshingId: string | null;
  onEdit: (config: SystemMiscConfig) => void;
  onRefresh: (config: SystemMiscConfig) => void;
}

export function SystemMiscActions({ config, refreshingId, onEdit, onRefresh }: SystemMiscActionsProps) {
  const { t } = useI18n();
  const isRefreshing = refreshingId === String(config.id);

  return (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('systemMisc.tooltips.editConfiguration')} position="top">
        <button
          type="button"
          onClick={() => onEdit(config)}
          className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-100/50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/30 dark:hover:text-blue-300"
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip
        content={isRefreshing ? t('systemMisc.refreshing') : t('systemMisc.tooltips.refreshStatus')}
        position="top"
      >
        <button
          type="button"
          onClick={() => onRefresh(config)}
          disabled={isRefreshing}
          className="rounded p-1 text-green-600 transition-colors hover:bg-green-100/50 hover:text-green-700 disabled:cursor-not-allowed disabled:opacity-60 dark:text-green-400 dark:hover:bg-green-900/30 dark:hover:text-green-300"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </SimpleTooltip>
    </div>
  );
}

